/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// State
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';

import { TConversionFactor } from '@global-types/api/api';
import {
	TConversionFactorQueryParams,
	TParsedConversionFactor,
} from '../types';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface IConversionFactorSlice {
	/* Pagination */
	conversionFactorsTotalPages: number;
	conversionFactorsPage: number;
	conversionFactorsTotal: number;
	conversionFactorsPageSize: number;

	/* Datatable */
	conversionFactorsSelectedRecords: TParsedConversionFactor[];
	conversionFactorsOpenConfirmationModalDelete: {
		open: boolean;
		params: {
			id?: string;
		};
	};
	conversionFactorsQueryParams: TConversionFactorQueryParams;
	conversionFactorsSortStatus: Partial<TConversionFactor>;
	conversionFactorsSortParams: string;
	conversionFactorsSelectedRow: Partial<TParsedConversionFactor>;
	conversionFactorsLastAccessTokenCreated: string | null;
	apiTokenOrganization: string | null;
	setConversionFactorsSelectedRow: (
		_conversionFactorsSelectedRow: Partial<TParsedConversionFactor>
	) => void;

	setConversionFactorOrganization: (
		_organizationId: string | null
	) => void;
	setConversionFactorsPage: (
		_conversionFactorsPage: number
	) => void;
	setConversionFactorsSelectedRecords: (
		_conversionFactorsSelectedRecords: TParsedConversionFactor[]
	) => void;
	setConversionFactorsOpenConfirmationModalDelete: (_conversionFactorsOpenConfirmationModalDelete: {
		open: boolean;
		params: {
			id?: string;
		};
	}) => void;
	setConversionFactorsQueryParams: (
		_conversionFactorsQueryParams: TConversionFactorQueryParams
	) => void;
	setConversionFactorsSortStatus: (
		_conversionFactorsSortStatus: Partial<TConversionFactor>
	) => void;
	setConversionFactorsSortParams: (
		_conversionFactorsSortParams: string
	) => void;

	setConversionFactorsPageSize: (
		_pageSize: number
	) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

const initialState = {
	/* Pagination */
	conversionFactorsTotal: 0,
	conversionFactorsPage: 1,
	conversionFactorsPageSize: 100,
	conversionFactorsTotalPages: 0,
	/**************/
	conversionFactors: [],
	conversionFactorsSelectedRecords: [],
	conversionFactorsQueryParams: {},
	conversionFactorsSelectedRow: {},
	conversionFactorsSortParams: '-createdAt',
	apiTokenOrganization: null,
	conversionFactorsSortStatus: {},
	conversionFactorsLastAccessTokenCreated: null,
	conversionFactorsOpenConfirmationModalDelete: {
		id: '',
		open: false,
		params: '',
	},
	conversionFactorsOpenConfirmationModalVerify: {
		open: false,
		params: '',
	},
	conversionFactorsOpenConfirmationModalMint: {
		open: false,
		params: '',
	},
};

export const createConversionFactorsSlice: StateCreator<
	TStore,
	[],
	[],
	IConversionFactorSlice
> = (set) => ({
	...initialState,
	setConversionFactorsSelectedRow: (
		_conversionFactorsSelectedRow: Partial<TParsedConversionFactor>
	) =>
		set({
			conversionFactorsSelectedRow:
				_conversionFactorsSelectedRow,
		}),
	setConversionFactorOrganization: (
		_organizationId: string | null
	) => set({ apiTokenOrganization: _organizationId }),
	setConversionFactorsLastAccessTokenCreated: (
		_conversionFactorsLastAccessTokenCreated: string
	) =>
		set({
			conversionFactorsLastAccessTokenCreated:
				_conversionFactorsLastAccessTokenCreated,
		}),

	setConversionFactorsSortParams: (_sortStatus: string) =>
		set({ conversionFactorsSortParams: _sortStatus }),
	setConversionFactorsSortStatus: (
		_sortStatus: Partial<TConversionFactor>
	) => set({ conversionFactorsSortStatus: _sortStatus }),
	setConversionFactorsQueryParams: (
		_queryParams: TConversionFactorQueryParams
	) =>
		set({ conversionFactorsQueryParams: _queryParams }),
	setConversionFactorsTotal: (_total: number) =>
		set({ conversionFactorsTotal: _total }),
	setConversionFactorsOpenConfirmationModalDelete:
		(_conversionFactorsOpenConfirmationModalDelete: {
			open: boolean;
			params: {
				id?: string;
			};
		}) =>
			set({
				conversionFactorsOpenConfirmationModalDelete:
					_conversionFactorsOpenConfirmationModalDelete,
			}),
	setConversionFactorsPage: (_page: number) =>
		set({ conversionFactorsPage: _page }),
	setConversionFactorsPageSize: (_pageSize: number) =>
		set({ conversionFactorsPageSize: _pageSize }),
	setConversionFactorsTotalPages: (_totalPages: number) =>
		set({ conversionFactorsTotalPages: _totalPages }),
	setConversionFactorsSelectedRecords: (
		_conversionFactorsSelectedRecords: TParsedConversionFactor[]
	) =>
		set({
			conversionFactorsSelectedRecords:
				_conversionFactorsSelectedRecords,
		}),
});
